import {
  migrateLegacyKey,
  readStoredObject,
  storageKey,
  storedText,
  writeStored,
} from '@/lib/storage';
import type { SignInDetails, VoiceMode } from '@/protocol/frames';
import { DEFAULT_MODEL_ID, modelChoice } from './models';
import { DEFAULT_PROVIDER, type ProviderChoice } from './tts';

/**
 * Where the gateway is, who we say we are, and which model answers. All runtime facts rather than
 * build-time constants: one image is pointed at a backend by env var, a tester can retarget it from
 * the settings dialog without a rebuild, and `?ws=` / `?model=` pin them for a one-off.
 */

interface RuntimeConfig {
  /** Absolute `wss://…` URL, or a same-origin path like `/ws`. */
  gatewayUrl?: string;
  householdId?: string;
  receiverId?: string;
  zipcode?: string;
  dma?: string;
  userId?: string;
  entitlementId?: string;
  /** Must be `compact` or `fullScreen`; anything else falls back to the built-in default. */
  voiceMode?: string;
}

declare global {
  interface Window {
    RUNTIME_CONFIG?: RuntimeConfig;
  }
}

export interface EndpointPreset {
  id: string;
  label: string;
  url: string;
  note: string;
}

/**
 * The Grasshopper team's deployed backend. Reached straight from the browser: it is a public
 * endpoint with no authentication on the handshake and no origin restriction, so no proxy of ours
 * sits in the path. It is also the only backend that emits the `debug` frames the latency panel
 * is built from.
 */
const ANDROMEDA_URL = 'wss://dgh-andromeda.dishanywhere.com/ws';

export const ENDPOINT_PRESETS: EndpointPreset[] = [
  {
    id: 'andromeda',
    label: 'Andromeda (Grasshopper)',
    url: ANDROMEDA_URL,
    note: 'Their deployed backend. Emits debug telemetry.',
  },
  {
    id: 'same-origin',
    label: 'Our test backend',
    url: '/ws',
    note: 'Ours, via /ws — the local gateway in dev, our sandbox deployment when deployed.',
  },
];

const DEFAULT_SIGN_IN: SignInDetails = {
  householdId: 'DEMO-HOUSEHOLD-1',
  receiverId: 'DEMO-RECEIVER-1',
  zipcode: '80112',
  dma: '751',
  userId: 'DEMO-USER-1',
  entitlementId: 'DEMO-ENTITLEMENT-1',
};

/** The voice experience display mode sent at sign-in, absent a runtime override. */
const DEFAULT_VOICE_MODE: VoiceMode = 'fullScreen';

const VOICE_MODES: readonly VoiceMode[] = ['compact', 'fullScreen'];

export const SETTINGS_KEY = storageKey('settings');

/** What builds before the namespaced keys wrote. Read once, so a tester's identity survives. */
const LEGACY_SETTINGS_KEY = 'voice-console.settings';

/**
 * Marks that the one-time endpoint reset below has happened, so a tester who then picks the
 * same-origin preset keeps it. Its contents are irrelevant; its presence is the flag.
 */
const ENDPOINT_RESET_KEY = storageKey('endpoint-reset');

/** A same-origin path becomes a ws:// URL against this page; an absolute URL passes through. */
export function resolveEndpoint(raw: string): string {
  const trimmed = raw.trim();
  if (/^wss?:\/\//i.test(trimmed)) return trimmed;

  const url = new URL(trimmed || '/ws', window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/** What the image was deployed pointing at, before any tester edited it. */
export function deployedEndpoint(): string {
  return window.RUNTIME_CONFIG?.gatewayUrl?.trim() || ANDROMEDA_URL;
}

export function defaultSignInDetails(): SignInDetails {
  const runtime = window.RUNTIME_CONFIG ?? {};
  return {
    householdId: runtime.householdId?.trim() || DEFAULT_SIGN_IN.householdId,
    receiverId: runtime.receiverId?.trim() || DEFAULT_SIGN_IN.receiverId,
    zipcode: runtime.zipcode?.trim() || DEFAULT_SIGN_IN.zipcode,
    dma: runtime.dma?.trim() || DEFAULT_SIGN_IN.dma,
    userId: runtime.userId?.trim() || DEFAULT_SIGN_IN.userId,
    entitlementId: runtime.entitlementId?.trim() || DEFAULT_SIGN_IN.entitlementId,
  };
}

/**
 * The voice experience display mode a fresh session signs in with: a runtime override when it
 * names one of the two modes the backend accepts, the built-in default otherwise — matched
 * case-sensitively, since the two spellings the backend validates against are exact.
 */
export function defaultVoiceMode(): VoiceMode {
  const configured = window.RUNTIME_CONFIG?.voiceMode?.trim();
  return (VOICE_MODES as readonly string[]).includes(configured ?? '')
    ? (configured as VoiceMode)
    : DEFAULT_VOICE_MODE;
}

export interface StoredSettings {
  endpoint: string;
  signIn: SignInDetails;
  /** The voice experience display mode sent at sign-in. Editable alongside the sign-in fields. */
  voiceMode: VoiceMode;
  /**
   * Whether a talked-over reply's remaining frames are dropped. Off models a client that stops its
   * audio on an interruption but reads every text frame as it arrives, which is what the STB is
   * expected to do; on is this client's own behaviour. Audio is silenced either way.
   */
  dropAbandonedText: boolean;
  /** `provider:model`, or `andromeda-default` to name none. See session/models.ts. */
  modelId: string;
  /** The TTS provider, or `andromeda-default` to name none. See session/tts.ts. */
  ttsProvider: ProviderChoice;
  /** The TTS model, where the provider has one; empty otherwise. */
  ttsModel: string;
  /** The TTS voice the chosen provider offers; empty for the backend default. */
  ttsVoice: string;
}

/**
 * A `?ws=` or `?model=` query wins outright, so a link can pin an endpoint or a model. Below that a
 * tester's own edit persists across reloads, which is the point of editing it — the dialog shows the
 * deployed default alongside so a stale one is visible rather than mysterious.
 */
export function loadSettings(): StoredSettings {
  migrateLegacySettings();

  const query = new URLSearchParams(window.location.search);
  const stored = readStoredObject(SETTINGS_KEY);

  return {
    endpoint: storedText(query.get('ws')) ?? storedText(stored?.endpoint) ?? deployedEndpoint(),
    signIn: storedSignIn(stored?.signIn),
    voiceMode: storedVoiceMode(stored?.voiceMode),
    // Defaults on, so the console behaves as it always has until a tester asks for the other side.
    dropAbandonedText: stored?.dropAbandonedText !== false,
    modelId: storedModelId(query.get('model'), stored?.modelId),
    ...storedTts(query, stored),
  };
}

/**
 * Carries what an older build stored under its own key, and drops the stored endpoint once: a local
 * run that had picked the same-origin `/ws` preset would otherwise keep starting there, where the
 * default is the shared Andromeda deployment whether the console is served locally or not. The reset
 * happens on this build's first load and never again, so an endpoint chosen after it sticks.
 */
function migrateLegacySettings(): void {
  migrateLegacyKey(LEGACY_SETTINGS_KEY, SETTINGS_KEY);
  if (readStoredObject(ENDPOINT_RESET_KEY) !== null) return;

  writeStored(ENDPOINT_RESET_KEY, { at: new Date().toISOString() });

  const stored = readStoredObject(SETTINGS_KEY);
  if (stored === null || !('endpoint' in stored)) return;
  const kept = { ...stored };
  delete kept.endpoint;
  writeStored(SETTINGS_KEY, kept);
}

export function saveSettings(settings: StoredSettings): void {
  writeStored(SETTINGS_KEY, settings);
}

/** Stored identity fields override the defaults one by one; a missing or blank one does not. */
function storedSignIn(value: unknown): SignInDetails {
  const defaults = defaultSignInDetails();
  if (value === null || typeof value !== 'object') return defaults;

  const stored = value as Record<string, unknown>;

  return {
    householdId: storedText(stored.householdId) ?? defaults.householdId,
    receiverId: storedText(stored.receiverId) ?? defaults.receiverId,
    zipcode: storedText(stored.zipcode) ?? defaults.zipcode,
    dma: storedText(stored.dma) ?? defaults.dma,
    userId: storedText(stored.userId) ?? defaults.userId,
    entitlementId: storedText(stored.entitlementId) ?? defaults.entitlementId,
  };
}

function storedVoiceMode(value: unknown): VoiceMode {
  return (VOICE_MODES as readonly unknown[]).includes(value)
    ? (value as VoiceMode)
    : defaultVoiceMode();
}

/**
 * The first of the pin and the stored value that names a choice this console can sign in with: a
 * known provider and a model, or the backend's own default. An id whose model this build does not
 * list is still honoured — it is how a model newer than the catalog is tried — but one whose provider
 * the gateway would refuse outright is not worth carrying into a handshake.
 */
function storedModelId(pinned: string | null, stored: unknown): string {
  for (const candidate of [storedText(pinned), storedText(stored)]) {
    if (candidate !== null && modelChoice(candidate) !== null) return candidate;
  }
  return DEFAULT_MODEL_ID;
}

/** The provider ids this build knows by name, so a stored one can be narrowed without the catalogue. */
const KNOWN_PROVIDERS: readonly ProviderChoice[] = [
  DEFAULT_PROVIDER,
  'polly',
  'elevenlabs',
  'gemini',
];

/**
 * The TTS provider, model and voice a fresh session is seeded with: a `?ttsProvider=` /
 * `?ttsModel=` / `?ttsVoice=` pin first, then the stored values, then the backend default. The
 * model and voice are stored verbatim and only reconciled against the live catalogue once it has
 * loaded (resolveTts drops a stale pair when the handshake is built), so this needs no catalogue.
 *
 * @param query  the page's query parameters, which may pin any of the three
 * @param stored the stored settings object, or null
 * @returns the three fields to seed the store with
 */
function storedTts(
  query: URLSearchParams,
  stored: Record<string, unknown> | null,
): Pick<StoredSettings, 'ttsProvider' | 'ttsModel' | 'ttsVoice'> {
  const rawProvider = storedText(query.get('ttsProvider')) ?? storedText(stored?.ttsProvider);
  const provider: ProviderChoice = KNOWN_PROVIDERS.includes(rawProvider as ProviderChoice)
    ? (rawProvider as ProviderChoice)
    : DEFAULT_PROVIDER;

  if (provider === DEFAULT_PROVIDER) {
    return { ttsProvider: DEFAULT_PROVIDER, ttsModel: '', ttsVoice: '' };
  }

  return {
    ttsProvider: provider,
    ttsModel: storedText(query.get('ttsModel')) ?? storedText(stored?.ttsModel) ?? '',
    ttsVoice: storedText(query.get('ttsVoice')) ?? storedText(stored?.ttsVoice) ?? '',
  };
}
